import React, {createContext, useState, useContext, useEffect} from 'react';
import {Outlet} from "react-router-dom";
import AuthProvider from "../services/AuthProvider";

import {Activity, Archive, Award, Box, Briefcase, CheckSquare, Clipboard, Code, CreditCard, FileText, Folder, Globe, Home as HomeIcon, Key, Layers, List, MapPin, Menu, Package, Shield, Sliders, Smartphone, Sun, Tag, Tool, Truck, User, Users, X, Zap, ChevronRight} from 'react-feather';

import { CustomProvider } from 'rsuite';
import ptBR from 'rsuite/locales/pt_BR';
import 'rsuite/dist/rsuite.min.css';

import './Layout.scss'
import { ModalStackProvider } from './Core/ModalStackProvider';
import { WindowManagerProvider, WindowRenderer } from './Window/WindowManager';
import { NotificationProvider } from './Core/NotificationProvider';

import {ReactComponent as Logo} from '../assets/logo.svg';
import classNames from "classnames";

import Dashboard2 from "../pages/Dashboard2";
import NewAbstractGrid from "./Core/NewAbstractGrid";
import Grid from "./Core/Grid";
import ContratoForm from "../pages/ContratoForm";
import ContratoFinanceiroForm from "../pages/ContratoFinanceiroForm";
import { v4 as uuidv4 } from 'uuid';
import {LogOut} from 'react-feather';
import OrcamentoRapido from "../pages/OrcamentoRapido";
import NotaFiscalImportForm from "../pages/NotaFiscalImportForm";
import SelectSandbox from "../pages/Sandbox/SelectSandbox";
import WindowsSandbox from "../pages/Sandbox/WindowsSandbox";
import ButtonsSandbox from "../pages/Sandbox/ButtonsSandbox";
import InputsSandbox from "../pages/Sandbox/InputsSandbox";

const MENU_PADRAO_ATIVO = 'Boletos';

// Helper function to find menu by name (searches recursively in children)
export const findMenuByName = (menuName) => {
    for (const menu of menus) {
        if (menu.name === menuName) {
            return menu;
        }
        if (menu.children) {
            const found = menu.children.find(child => child.name === menuName);
            if (found) {
                return found;
            }
        }
    }
    return null;
};

export const menus = [
    {
        section: 'Geral',
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Dashboard',
        icon: <HomeIcon/>,
        component: React.memo(Dashboard2),
        props: {},
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Atividades',
        icon: <Activity/>,
        component: Grid,
        props: {router: 'atividade'},
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Orçamento Rápido',
        icon: <Zap/>,
        component: React.memo(OrcamentoRapido),
        props: {},
        allowed: [1, 2, 3, 4]
    },

    {
        section: 'Comercial',
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Orçamentos',
        icon: <List/>,
        component: Grid,
        props: {router: 'orcamento'},
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Projetos',
        icon: <Briefcase/>,
        component: Grid,
        props: {router: 'contrato'},
        allowed: [1, 4]
    },
    {
        name: 'Clientes',
        icon: <Users/>,
        component: Grid,
        props: {router: 'cliente'},
        allowed: [1, 2, 3, 4]
    },
    {
        name: 'Kits',
        icon: <Package/>,
        component: Grid,
        props: {router: 'kit'},
        allowed: [1, 4]
    },
    {
        name: 'Overloads',
        icon: <Sliders/>,
        component: Grid,
        props: {router: 'overload'},
        allowed: [1]
    },

    {
        section: 'Financeiro',
        allowed: [1, 4]
    },
    {
        name: 'Boletos',
        icon: <CreditCard/>,
        component: Grid,
        props: {router: 'boleto'},
        allowed: [1, 4]
    },
    {
        name: 'Em desenvolvimento',
        menuKey: 'financeiro-dev',
        icon: <Tool/>,
        allowed: [1],
        children: [
            {
                name: 'Contas a Receber',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'contaReceber'},
                allowed: [1]
            },
            {
                name: 'Recebimentos',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'recebimento'},
                allowed: [1]
            },
            {
                name: 'Contas a Pagar',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'contaPagar'},
                allowed: [1]
            },
            {
                name: 'Pagamentos',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'pagamento'},
                allowed: [1]
            },
            {
                name: 'Conciliação',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'contaReceber'},
                allowed: [1]
            },
            {
                name: 'Provisões',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'provisao'},
                allowed: [1]
            },
            {
                name: 'Contas Financeiras',
                icon: <Box/>,
                component: Grid,
                props: {router: 'contaFinanceira'},
                allowed: [1]
            },
        ]
    },

    // Módulo em desenvolvimento: visível só para o master.
    {
        section: 'Produtos',
        allowed: [1]
    },
    {
        name: 'Produtos',
        icon: <Package/>,
        component: Grid,
        props: {router: 'produto'},
        allowed: [1]
    },
    {
        name: 'Estoque',
        icon: <Archive/>,
        component: Grid,
        props: {router: 'estoque'},
        allowed: [1]
    },
    {
        name: 'Cadastros',
        menuKey: 'produtos-cadastros',
        icon: <List/>,
        allowed: [1],
        children: [
            {
                name: 'Marcas',
                icon: <Award/>,
                component: Grid,
                props: {router: 'marca'},
                allowed: [1]
            },
            {
                name: 'Categorias',
                icon: <Folder/>,
                component: Grid,
                props: {router: 'categoria'},
                allowed: [1]
            },
            {
                name: 'Características',
                icon: <Sliders/>,
                component: Grid,
                props: {router: 'caracteristica'},
                allowed: [1]
            },
            {
                name: 'Depósitos',
                icon: <Box/>,
                component: Grid,
                props: {router: 'deposito'},
                allowed: [1]
            },
        ]
    },

    {
        section: 'Faturamento',
        allowed: [1, 4]
    },
    {
        name: 'Notas Fiscais de Saída',
        icon: <FileText/>,
        component: Grid,
        props: {router: 'notaFiscalServico'},
        allowed: [1, 4]
    },
    {
        name: 'Em desenvolvimento',
        menuKey: 'faturamento-dev',
        icon: <Tool/>,
        allowed: [1],
        children: [
            {
                name: 'Notas Fiscais de Entrada',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'notaFiscalServico'},
                allowed: [1]
            },
            {
                name: 'Emitentes v2',
                icon: <Briefcase/>,
                component: Grid,
                props: {router: 'emitente'},
                allowed: [1]
            },
            {
                name: 'Notas Fiscais de Saída v2',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'notafiscal'},
                allowed: [1]
            },
            {
                name: 'Notas Fiscais de Entrada v2',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'notafiscal'},
                allowed: [1]
            },
            {
                name: 'Importar NF-e Entrada v2',
                icon: <Package/>,
                component: React.memo(NotaFiscalImportForm),
                props: {},
                allowed: [1]
            },
        ]
    },

    {
        section: 'Usinas',
        allowed: [1, 4]
    },
    {
        name: 'Usinas',
        icon: <Sun/>,
        component: Grid,
        props: {router: 'usina'},
        allowed: [1, 4]
    },
    {
        name: 'Vínculos com Projetos',
        icon: <Briefcase/>,
        component: Grid,
        props: {router: 'usinacontrato'},
        allowed: [1, 4]
    },
    {
        name: 'Histórico de Geração',
        icon: <Activity/>,
        component: Grid,
        props: {router: 'usinageracaosumario'},
        allowed: [1, 4]
    },

    {
        section: 'Cadastros',
        allowed: [1, 3, 4]
    },
    {
        name: 'Vendedores',
        icon: <User/>,
        component: Grid,
        props: {router: 'vendedor'},
        allowed: [1, 3, 4]
    },
    {
        name: 'Fornecedores',
        icon: <Truck/>,
        component: Grid,
        props: {router: 'fornecedor'},
        allowed: [1, 4]
    },
    {
        name: 'Cidades',
        icon: <MapPin/>,
        component: Grid,
        props: {router: 'cidade'},
        allowed: [1, 4]
    },
    {
        name: 'Tags',
        icon: <Tag/>,
        component: Grid,
        props: {router: 'tag'},
        allowed: [1, 4]
    },
    {
        name: 'Posts do Site',
        icon: <Globe/>,
        component: Grid,
        props: {router: 'site/posts'},
        allowed: [1, 4]
    },

    {
        section: 'Sistema',
        allowed: [1]
    },
    // App de campo (módulo Obras): em desenvolvimento, só o master.
    {
        name: 'App',
        menuKey: 'app-campo',
        icon: <Smartphone/>,
        allowed: [1],
        children: [
            {
                name: 'Obras',
                icon: <HomeIcon/>,
                component: Grid,
                props: {router: 'tarobra'},
                allowed: [1]
            },
            {
                name: 'Tarefas',
                icon: <Clipboard/>,
                component: Grid,
                props: {router: 'tartarefa'},
                allowed: [1]
            },
            {
                name: 'Instaladores',
                icon: <Tool/>,
                component: Grid,
                props: {router: 'uninstalador'},
                allowed: [1]
            },
            {
                name: 'Pessoas',
                icon: <Users/>,
                component: Grid,
                props: {router: 'unpessoa'},
                allowed: [1]
            },
            {
                name: 'Usuários',
                icon: <Key/>,
                component: Grid,
                props: {router: 'unusuario'},
                allowed: [1]
            },
            {
                name: 'Perfis',
                icon: <Shield/>,
                component: Grid,
                props: {router: 'unperfil'},
                allowed: [1]
            },
            {
                name: 'Tipos de Tarefa',
                icon: <Layers/>,
                component: Grid,
                props: {router: 'tartipo'},
                allowed: [1]
            },
            {
                name: 'Checklists',
                icon: <CheckSquare/>,
                component: Grid,
                props: {router: 'tartemplate'},
                allowed: [1]
            },
            {
                name: 'Modelos de Contrato',
                icon: <FileText/>,
                component: Grid,
                props: {router: 'uncontratomodelo'},
                allowed: [1]
            },
            // Contratos não têm item próprio: contrato é sempre de um instalador, e
            // se chega por ele, pela ação "Contratos" do grid de Instaladores.
        ]
    },
    {
        name: 'Dev',
        icon: <Code/>,
        allowed: [1],
        children: [
            {
                name: 'Select Sandbox',
                icon: <Box/>,
                component: React.memo(SelectSandbox),
                props: {},
                allowed: [1]
            },
            {
                name: 'Windows Sandbox',
                icon: <Box/>,
                component: React.memo(WindowsSandbox),
                props: {},
                allowed: [1]
            },
            {
                name: 'Buttons Sandbox',
                icon: <Box/>,
                component: React.memo(ButtonsSandbox),
                props: {},
                allowed: [1]
            },
            {
                name: 'Inputs Sandbox',
                icon: <Box/>,
                component: React.memo(InputsSandbox),
                props: {},
                allowed: [1]
            },
        ]
    },
];

const LayoutContext = createContext(null);

export const useLayout = () => useContext(LayoutContext);

export const LayoutProvider = ({children}) => {
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState();
    const [showMenu, setShowMenu] = useState(false);
    const [tabsRefs, setTabsRefs] = useState([]);
    const [expandedMenus, setExpandedMenus] = useState({});

    const toggleMenu = () => setShowMenu((prevState) => !prevState);

    const toggleMenuExpand = (menuName, forceState) => {
        setExpandedMenus((prev) => ({
            ...prev,
            [menuName]: forceState !== undefined ? forceState : !prev[menuName]
        }));
    };

    const closeTab = React.useCallback((menu) => {
        const newTabs = [...tabs].filter((tab) => {
            return tab.uuid !== menu.uuid
        });
        const lastTab = newTabs.at(-1);
        if(lastTab) {
            setActiveTab(lastTab.uuid);
        }
        setTabs(newTabs);
    }, [tabs]);

    const openMenu = React.useCallback((item, forceNew = false) => {
        let exists = null;
        tabs.map((tabItem, tabIndex) => {
            if (tabItem.name == item.name) {
                exists = tabItem.uuid;
            }
        });

        if (exists && !forceNew) {
            setActiveTab(exists);
        } else {
            const newTab = {
                name: item.name,
                icon: item.icon,
                component: item.component,
                uuid: uuidv4(),
                props: {
                    ...item.props
                }
            };

            const newTabs = [...tabs, newTab];
            setTabs(newTabs);
            setActiveTab(newTab.uuid);
        }
    }, [tabs]);

    return (
        <LayoutContext.Provider value={{
            tabs, activeTab, setActiveTab,
            menus, showMenu, toggleMenu, openMenu, closeTab,
            tabsRefs, setTabsRefs,
            expandedMenus, toggleMenuExpand
        }}>
            {children}
        </LayoutContext.Provider>
    );
}

export const withLayout = (Child) => (props) => (
    <LayoutContext.Consumer>
        {(context) => <Child {...props} {...context} />}
    </LayoutContext.Consumer>
);



const UserInfo = () => {
    const { triggerLogout, user } = useContext(AuthProvider);

    return <div className="user-info">
        <div className="user-name">{user.nome}</div>
        <button onClick={() => triggerLogout()}><LogOut /></button>
    </div>
}

const LayoutView = (LayoutProps) => {
    const {showMenu, toggleMenu, menus, tabs, activeTab, setActiveTab, openMenu, closeTab, tabsRefs, setTabsRefs, expandedMenus, toggleMenuExpand} = LayoutProps;
    const { user } = useContext(AuthProvider);

    // const methods = useForm();
    // const allData = methods.watch();

    // DEV: Abre Projetos automaticamente para testes
    // useEffect(() => {
    //     if(tabs.length === 0) {
    //         const clientes = findMenuByName(MENU_PADRAO_ATIVO);
    //         if (clientes) {
    //             openMenu(clientes);
    //         }
    //     }
    // }, []);

    return <>
        <nav id="sidebarMenu" className={"sidebar " + (showMenu ? 'menu-show' : '')}>
            <header>
                <div className="logo">
                    <Logo/>
                    <div className="logo-typo">
                        <b>Argon</b>
                        <span>Solar</span>
                    </div>
                </div>
                <button className="menu-toggle" onClick={toggleMenu}>{showMenu ? <X/> : <Menu/>}</button>
            </header>

            <ul className="nav">
                {
                    menus && menus.map((item, itemIdx) => {
                        if(item.allowed){
                            if(user){
                                if(!item.allowed.includes(user?.acesso)){
                                    return null;
                                }
                            }
                        }
                        if(item.allowedUser){
                            if(user){
                                if(!item.allowedUser.includes(user?.id)){
                                    return null;
                                }
                            }
                        }

                        // Título de seção (não clicável)
                        if(item.section) {
                            return <li key={itemIdx} className="nav-section">{item.section}</li>;
                        }

                        // Se o item tem children, renderiza como acordeão
                        if(item.children) {
                            // menuKey permite dois submenus com o mesmo rótulo sem compartilhar o estado de expansão
                            const expandKey = item.menuKey || item.name;
                            const isExpanded = expandedMenus[expandKey];
                            return (
                                <li key={itemIdx}
                                    className={classNames('nav-item nav-item-parent', {'expanded': isExpanded})}>
                                    <a className="nav-link" href="#" onClick={(e) => {
                                        e.preventDefault();
                                        toggleMenuExpand && toggleMenuExpand(expandKey);
                                    }}>
                                        {item.icon}
                                        <span className="nav-label">{item.name}</span>
                                        <ChevronRight size={14} className="menu-chevron" />
                                    </a>
                                    {isExpanded && (
                                        <ul className="nav-submenu">
                                            {item.children.map((child, childIdx) => {
                                                if(child.allowed){
                                                    if(user){
                                                        if(!child.allowed.includes(user?.acesso)){
                                                            return null;
                                                        }
                                                    }
                                                }
                                                if(child.allowedUser){
                                                    if(user){
                                                        if(!child.allowedUser.includes(user?.id)){
                                                            return null;
                                                        }
                                                    }
                                                }
                                                return (
                                                    <li key={`${itemIdx}-${childIdx}`} className="nav-item nav-item-child">
                                                        <a className="nav-link" href="#" onClick={(e) => {
                                                            e.preventDefault();
                                                            openMenu && openMenu(child);
                                                            toggleMenu();
                                                        }}>
                                                            {child.icon}
                                                            <span className="nav-label">{child.name}</span>
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        }

                        // Se não tem children, renderiza normalmente
                        return <li key={itemIdx} className="nav-item">
                                <a className="nav-link" href="#" onClick={(e) => {
                                    e.preventDefault();
                                    openMenu && openMenu(item);
                                    toggleMenu();
                                }}>
                                    {item.icon}
                                    <span className="nav-label">{item.name}</span>
                                </a>
                            </li>
                        }
                    )
                }
            </ul>

            <UserInfo />
        </nav>
        <main>

            {/*<FormProvider {...methods} >*/}
            {/*    <UiSelectPicker size={12} name="tag2" label="Tags" router="tag" idField="id" titleField="titulo"/>*/}
            {/*    <pre>{JSON.stringify(allData, null, 2)}</pre>*/}
            {/*</FormProvider>*/}

            <nav className="nav nav-tabs">
                {tabs && tabs.map((tab) => {
                    return (
                        <div key={tab.uuid}
                             onMouseDown={(event) => {
                                 if (event.button == 1) {
                                     event.preventDefault();
                                     closeTab && closeTab(tab);
                                 }
                             }}
                             onClick={(event) => {
                                 setActiveTab && setActiveTab(tab.uuid);
                             }} className={classNames('nav-link', {
                            'active': activeTab == tab.uuid
                        })}>
                            {tab.name}
                            <div className="nav-link-close" onClick={(event) => {
                                event.stopPropagation();
                                closeTab && closeTab(tab)
                            }}>
                                <X/>
                            </div>
                        </div>
                    )
                })}
            </nav>
            <div className="tab-content">
                {tabs && tabs.map((tab) => {
                    const TabContentComponent = tab.component;
                    return (
                        <div key={'tab-content-' + tab.uuid} ref={(contentRef) => {
                            setTabsRefs && setTabsRefs((prevValue) => {
                                prevValue[tab.uuid] = contentRef;
                                return prevValue;
                            });
                        }} className={classNames('tab-pane', {
                            'show active': activeTab == tab.uuid
                        })} id={'tab-' + tab.uuid}>
                            <TabContentComponent {...tab.props} {...LayoutProps} />
                        </div>
                    )
                })}
            </div>
            <Outlet/>
        </main>
    </>
}

const LayoutWithContext = withLayout(LayoutView);

const Layout = () => {
    return (
        <CustomProvider locale={ptBR}>
            <LayoutProvider>
                <WindowManagerProvider>
                    <NotificationProvider>
                        <ModalStackProvider>
                            <WindowRenderer />
                            <LayoutWithContext/>
                        </ModalStackProvider>
                    </NotificationProvider>
                </WindowManagerProvider>
            </LayoutProvider>
        </CustomProvider>
    );

}

export default Layout;
